import type { ChangeEvent, ReactNode } from 'react'

/**
 * القطعُ المشتركةُ للشاشات — <b>ولا لونَ يُكتب هنا مباشرةً</b>: الرموزُ في
 * `index.css`، فيومَ يتغيّر لونُ التحذير يتغيّر في موضعٍ واحد.
 */

export function Panel({ title, children, actions }: {
  title?: ReactNode
  children: ReactNode
  actions?: ReactNode
}) {
  return (
    <section className="rounded-lg border border-line bg-surface-raised p-4 sm:p-5">
      {(title || actions) && (
        <header className="mb-4 flex flex-wrap items-center justify-between gap-2">
          {title && <h2 className="text-base font-semibold">{title}</h2>}
          {actions}
        </header>
      )}
      {children}
    </section>
  )
}

type Tone = 'info' | 'ok' | 'warn' | 'danger'

const toneStyles: Record<Tone, string> = {
  info: 'border-line bg-surface text-ink-soft',
  ok: 'border-ok/40 bg-ok/10 text-ink',
  warn: 'border-warn/50 bg-warn/10 text-ink',
  danger: 'border-danger/50 bg-danger/10 text-ink',
}

export function Banner({ tone = 'info', children }: { tone?: Tone; children: ReactNode }) {
  return (
    <div className={`rounded-md border px-3 py-2 text-sm leading-relaxed ${toneStyles[tone]}`} role="status">
      {children}
    </div>
  )
}

/**
 * عرضُ خطأٍ جاء من الخادم.
 *
 * <b>ويُعرض رمزُه معه</b> — لا زينةً: الموظّفُ يصوّر الشاشةَ ويرسلها، والرمزُ
 * (`intake.price_needs_permission`) يقول للمالك ما الناقصُ بالضبط، بينما
 * «تعذّر إتمامُ الطلب» لا تقول شيئًا.
 */
export function ErrorBanner({ error }: { error: unknown }) {
  if (!error) {
    return null
  }

  // ⚠ **يُقرأ الشكلُ ولا يُستورد الصنف.** ولو استوردت `ApiError` من
  //   `@shop/api-client` لَصارت حزمةُ العناصر البصريّة تعتمد على طبقة الشبكة —
  //   وهو بالضبط ما منعه المالك: `ui` للمكوّنات البصريّة قدرَ الإمكان.
  //
  //   والفحصُ على حقلين معًا لا على واحد: `Error` عاديٌّ لا `status` فيه ولا
  //   `code`، فلا يُعرض رمزُ خطأٍ ملفَّق تحت رسالةِ عطبِ شبكة.
  const shape = error as { status?: unknown; code?: unknown; message?: unknown }

  const api =
    typeof shape.status === 'number' && typeof shape.code === 'string'
      ? { status: shape.status, code: shape.code, message: String(shape.message ?? '') }
      : null

  return (
    <Banner tone="danger">
      <div>{api ? api.message : 'تعذّر الاتّصالُ بالخادم. تحقّق من الشبكة.'}</div>
      {api && (
        <div className="tabular mt-1 text-xs opacity-70" dir="ltr">
          {api.status} · {api.code}
        </div>
      )}
    </Banner>
  )
}

export function Spinner({ label = 'يُحمَّل…' }: { label?: string }) {
  return <p className="py-6 text-center text-sm text-ink-soft">{label}</p>
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="py-6 text-center text-sm text-ink-soft">{children}</p>
}

const inputClass =
  'w-full rounded-md border border-line bg-surface px-3 py-2 text-sm outline-none ' +
  'focus:border-brand focus:ring-2 focus:ring-brand/30 disabled:opacity-60'

export function Field({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-ink-soft">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-ink-soft">{hint}</span>}
    </label>
  )
}

export function TextInput({ value, onChange, ...rest }: {
  value: string
  onChange: (value: string) => void
  type?: string
  placeholder?: string
  disabled?: boolean
  autoFocus?: boolean
  dir?: 'rtl' | 'ltr'
  name?: string
  autoComplete?: string
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
}) {
  return (
    <input
      {...rest}
      className={inputClass}
      value={value}
      onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
    />
  )
}

/**
 * حقلٌ رقميّ — <b>ونصٌّ في الـDOM لا `type="number"`</b>.
 *
 * ‏`number` يبتلع ما لا يفهمه ويردّ `''`، فيمحو المتصفّحُ ما كتبه الموظّفُ بلا
 * رسالة؛ وعجلةُ الفأرة تغيّر السعرَ بمرورٍ عابر. والفاصلةُ العربيّة «٫» والعربيّةُ
 * الشرقيّة تُقرآن هنا، وهما ما يكتبه الموظّف فعلًا.
 */
export function NumberInput({ value, onChange, placeholder, disabled }: {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
}) {
  return (
    <input
      className={`${inputClass} tabular`}
      dir="ltr"
      inputMode="decimal"
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
    />
  )
}

export function Select({ value, onChange, children, disabled }: {
  value: string
  onChange: (value: string) => void
  children: ReactNode
  disabled?: boolean
}) {
  return (
    <select
      className={inputClass}
      value={value}
      disabled={disabled}
      onChange={(e: ChangeEvent<HTMLSelectElement>) => onChange(e.target.value)}
    >
      {children}
    </select>
  )
}

export function TextArea({ value, onChange, rows = 4, placeholder, disabled }: {
  value: string
  onChange: (value: string) => void
  rows?: number
  placeholder?: string
  disabled?: boolean
}) {
  return (
    <textarea
      className={`${inputClass} leading-relaxed`}
      rows={rows}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
    />
  )
}

const buttonBase =
  'inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium ' +
  'transition disabled:cursor-not-allowed disabled:opacity-50'

const buttonTones = {
  primary: 'bg-brand text-white hover:opacity-90',
  ghost: 'border border-line bg-surface hover:bg-surface-raised',
  danger: 'border border-danger/50 bg-danger/10 hover:bg-danger/20',
}

export function Button({ children, onClick, type = 'button', tone = 'ghost', disabled, title, wide }: {
  children: ReactNode
  onClick?: () => void
  type?: 'button' | 'submit'
  tone?: keyof typeof buttonTones
  disabled?: boolean
  title?: string
  wide?: boolean
}) {
  return (
    <button
      className={`${buttonBase} ${buttonTones[tone]} ${wide ? 'w-full' : ''}`}
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      {children}
    </button>
  )
}

/** سطرُ «عنوانٌ: قيمة» — أكثرُ ما يُكرَّر في شاشات العرض. */
export function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-line py-2 last:border-b-0">
      <span className="text-xs text-ink-soft">{label}</span>
      <span className="text-sm">{children}</span>
    </div>
  )
}
